import React, { useState, useEffect } from 'react';
import { TrendingUp, Briefcase, Sparkles, Edit, Save } from 'lucide-react';
import { useProfile } from '../../hooks/auth/useProfile';
import { supabase } from '../../lib/supabase';
import { toast } from 'react-hot-toast';

interface TrendData {
  trendingSkills: string[];
  inDemandRoles: string[];
  emergingRoles: string[];
}

const DEFAULT_TRENDS: TrendData = {
  trendingSkills: ['React Native', 'Rust', 'AI/ML', 'Web3'],
  inDemandRoles: ['DevOps Engineer', 'Full-Stack Developer', 'Data Scientist'],
  emergingRoles: ['AI Prompt Engineer', 'Web3 Developer', 'Cloud Architect'],
};

export function JobMarketTrends() {
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [trends, setTrends] = useState<TrendData>(DEFAULT_TRENDS);
  const { profile } = useProfile();
  const isAdmin = profile?.is_admin || false;

  useEffect(() => {
    loadTrends();
  }, []);

  const loadTrends = async () => {
    try {
      const { data, error } = await supabase
        .from('platform_settings')
        .select('settings')
        .eq('category', 'job_trends')
        .single();

      if (error && error.code !== 'PGRST116') {
        // Only throw if it's not a "no rows returned" error
        throw error;
      }
      
      if (data?.settings) {
        setTrends(data.settings as TrendData);
      } else {
        // If no data exists, create default trends
        const { error: insertError } = await supabase
          .from('platform_settings')
          .insert({
            category: 'job_trends',
            settings: DEFAULT_TRENDS
          });

        if (insertError) throw insertError;
      }
    } catch (error) {
      console.error('Error loading trends:', error);
      // Keep using default trends on error
    }
  };

  const handleSave = async () => {
    if (!isAdmin) return;
    
    // Clean up empty lines before saving
    const cleanedTrends = {
      trendingSkills: trends.trendingSkills.filter(Boolean),
      inDemandRoles: trends.inDemandRoles.filter(Boolean),
      emergingRoles: trends.emergingRoles.filter(Boolean)
    };
    
    setIsLoading(true);
    try {
      const { error } = await supabase
        .from('platform_settings')
        .upsert({
          category: 'job_trends',
          settings: cleanedTrends,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'category'
        });

      if (error) throw error;
      setTrends(cleanedTrends);
      toast.success('Job trends updated successfully');
      setIsEditing(false);
    } catch (error) {
      console.error('Error saving trends:', error);
      toast.error('Failed to save trends');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-[#2a1114] rounded-[8px] p-6 shadow-lg">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-2 text-white">
          <TrendingUp className="h-5 w-5 text-white" />
          <h2 className="text-lg font-medium text-white pb-2 border-b border-white">Latest Job Market Trends</h2>
        </div>
        {isAdmin && (
          <button
            onClick={() => setIsEditing(!isEditing)}
            className="text-white hover:opacity-80 flex items-center text-sm"
          >
            {isEditing ? (
              <>
                <Save className="h-4 w-4 mr-1" />
                Save
              </>
            ) : (
              <>
                <Edit className="h-4 w-4 mr-1" />
                Edit
              </>
            )}
          </button>
        )}
      </div>

      <div className="space-y-6">
        {/* Trending Skills */}
        <div>
          <h3 className="flex items-center text-sm font-medium text-white mb-3">
            <Sparkles className="h-4 w-4 mr-2 text-white" />
            <span className="font-bold">Trending Skills</span>
          </h3>
          {isEditing ? (
            <textarea
              className="w-full bg-gray-50 border border-gray-200 rounded-lg p-2 text-black text-sm"
              rows={5}
              value={trends.trendingSkills.join('\n')} 
              onChange={(e) => setTrends({
                ...trends,
                trendingSkills: e.target.value.split('\n').map(s => s.trim())
              })}
              placeholder="Enter one skill per line"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.stopPropagation();
                }
              }}
            />
          ) : (
            <div className="flex flex-wrap gap-2">
              {trends.trendingSkills.map((skill) => (
                <span
                  key={skill}
                  className="px-3 py-1 bg-white/10 text-white text-sm rounded-full"
                >
                  {skill}
                </span>
              ))}
            </div>
          )}
        </div>

        <div className="border-t border-gray-200"></div>
        {/* Roles Grid */}
        <div className="grid grid-cols-2 divide-x divide-white">
          {/* In-Demand Roles */}
          <div className="pr-6">
            <h3 className="flex items-center text-sm font-medium text-white mb-3">
              <Briefcase className="h-4 w-4 mr-2 text-white" />
              <span className="font-bold">In-Demand Roles</span>
            </h3>
            {isEditing ? (
              <textarea
                className="w-full bg-gray-50 border border-gray-200 rounded-lg p-2 text-black text-sm"
                rows={5}
                value={trends.inDemandRoles.join('\n')} 
                onChange={(e) => setTrends({
                  ...trends,
                  inDemandRoles: e.target.value.split('\n').map(s => s.trim())
                })}
                placeholder="Enter one role per line"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.stopPropagation();
                  }
                }}
              />
            ) : (
              <ul className="space-y-2">
                {trends.inDemandRoles.map((role) => (
                  <li key={role} className="text-white text-sm">{role}</li>
                ))}
              </ul>
            )}
          </div>

          {/* Emerging Roles */}
          <div className="pl-6">
            <h3 className="flex items-center text-sm font-medium text-white mb-3">
              <Sparkles className="h-4 w-4 mr-2 text-white" />
              <span className="font-bold">Emerging Roles</span>
            </h3>
            {isEditing ? (
              <textarea
                className="w-full bg-gray-50 border border-gray-200 rounded-lg p-2 text-black text-sm"
                rows={5}
                value={trends.emergingRoles.join('\n')} 
                onChange={(e) => setTrends({
                  ...trends,
                  emergingRoles: e.target.value.split('\n').map(s => s.trim())
                })}
                placeholder="Enter one role per line"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.stopPropagation();
                  }
                }}
              />
            ) : (
              <ul className="space-y-2">
                {trends.emergingRoles.map((role) => (
                  <li key={role} className="text-white text-sm">{role}</li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
      
      {isEditing && (
        <div className="mt-6 flex justify-end">
          <button
            onClick={() => {
              // Filter out empty lines before saving
              const cleanedTrends = {
                trendingSkills: trends.trendingSkills.filter(Boolean),
                inDemandRoles: trends.inDemandRoles.filter(Boolean),
                emergingRoles: trends.emergingRoles.filter(Boolean)
              };
              setTrends(cleanedTrends);
              handleSave();
            }}
            disabled={isLoading}
            className="px-4 py-2 bg-white text-[#2a1114] rounded-lg hover:opacity-90 transition-colors text-sm flex items-center"
          >
            {isLoading ? (
              <>Saving...</>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Save Changes
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
}